import type { Row } from "@rocicorp/zero";
import type {
  ConnectorDefinition,
  FieldTarget,
  StepItem,
  VisibleWhen,
} from "./types";

export type ConnectorWithOAuth = Row["connectedApps"] & {
  oauthConnection: Row["oauthConnections"] | undefined;
};

export type ConnectorFieldSource = Pick<
  ConnectorWithOAuth,
  | "name"
  | "urlPattern"
  | "configuration"
  | "fresh"
  | "oauthClientId"
  | "oauthRequestedScopes"
> & {
  oauthConnection: Pick<Row["oauthConnections"], "status"> | null | undefined;
};

export function getTargetValue(
  target: FieldTarget,
  app: ConnectorFieldSource
): string | undefined {
  switch (target.kind) {
    case "connectedApp":
      return app[target.field];
    case "configuration":
      return app.configuration[target.field];
    case "secrets":
      return undefined;
    case "oauthConnection":
      if (target.field === "scopes") return app.oauthRequestedScopes?.join(" ");
      if (target.field === "clientSecret") return undefined;
      if (target.field === "clientId") return app.oauthClientId ?? undefined;
      return app.oauthConnection?.[target.field] as string | undefined;
  }
}

export function targetIsSet(
  target: FieldTarget,
  app: ConnectorFieldSource
): boolean {
  // even though clientSecret is not replicated, we know it has been persisted if the connector is not fresh
  if (target.kind === "oauthConnection" && target.field === "clientSecret")
    return !app.fresh;
  return Boolean(getTargetValue(target, app));
}

export function targetsMatch(a: FieldTarget, b: FieldTarget): boolean {
  return a.kind === b.kind && a.field === b.field;
}

// OAuth endpoint URLs are often built by interpolating configuration fields
// (e.g. `https://${databricksInstance}/oidc/v1/token`), which still produces
// a syntactically valid URL when the field is unset — just pointed at a
// bogus host. Check the source fields instead of the resulting URL.
export function missingRequiredConfigurationFields(
  definition: Pick<ConnectorDefinition, "steps">,
  app: ConnectorFieldSource
): string[] {
  const missing: string[] = [];
  for (const step of definition.steps) {
    for (const item of step.items) {
      if (
        "target" in item &&
        item.target.kind === "configuration" &&
        "required" in item &&
        item.required &&
        isItemVisible(item, app, definition) &&
        !app.configuration[item.target.field]
      ) {
        missing.push(item.target.field);
      }
    }
  }
  return missing;
}

export function isItemVisible(
  item: StepItem,
  app: ConnectorFieldSource,
  definition: Pick<ConnectorDefinition, "steps">
): boolean {
  if (!("visibleWhen" in item) || !item.visibleWhen) return true;
  return item.visibleWhen.every((condition) =>
    isFieldVisible(condition, app, definition)
  );
}

function isFieldVisible(
  condition: VisibleWhen,
  app: ConnectorFieldSource,
  definition: Pick<ConnectorDefinition, "steps">
): boolean {
  let current = getTargetValue(condition.target, app);
  if (current === undefined) {
    for (const step of definition.steps) {
      for (const item of step.items) {
        if (
          "target" in item &&
          targetsMatch(item.target, condition.target) &&
          "defaultValue" in item &&
          item.defaultValue
        ) {
          current = item.defaultValue;
        }
      }
    }
  }
  if (condition.value !== undefined) return current === condition.value;
  if (condition.notValue !== undefined) return current !== condition.notValue;
  return current !== undefined && current !== "";
}
